import React from 'react';
import {Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import {NAVY, PageHeader, BandChip, GuidanceBanner, headCell, bodyCell, contextBandColor, heatFill} from '../components/widgets.comp';
import {CONTEXT_BAND_COLORS} from '../../../utils/constants';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, whole} from '../networkData';
import {isSuppressedCell} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';

/* Slide 27 — "Context & Support by Region", renamed "Context & Support by
   School" per the deck's "Replace region → school" note (SND-27-01, -02).

     SND-27-01  heat-map, school × area   SND.S_8c.C_2   TableRubricCard
     SND-27-02  area number → name list   the same card's columns

   Columns: School, Composite (COS_ContextAVG), then one per area with its own
   number (column_subname "CA1" …). Cell colour is the datahouse's band. The
   guidance names the school with the lowest composite, as the deck's does.
   Every column sorts from its header; filters appear once the network has
   enough schools to filter (QA 9 Oct 2026). */
const Legend = () => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2.5}}>
        {Object.keys(CONTEXT_BAND_COLORS).map((band) => (
            <BandChip key={band} label={band} color={CONTEXT_BAND_COLORS[band]}/>
        ))}
    </Box>
);

// Same fill as the School and National heat-maps (band colour at 0.8), navy figures.
const heat = (c) => ({
    ...bodyCell, textAlign: 'center', fontWeight: 700, fontSize: '14px', color: NAVY,
    backgroundColor: c.band ? heatFill(contextBandColor(c.band)) : undefined,
});
const show = (c) => (c.suppressed ? <SuppressedValue variant="cell"/> : (c.score ?? '—'));

// The table sits in its own component so the sort/filter hook runs below the page's loading branches.
const SchoolHeatmapTable = ({rows, areaCols}) => {
    const columns = [
        {key: 'school', label: 'School', type: 'text'},
        {key: 'composite', label: 'Composite', type: 'number', accessor: (r) => (r.composite?.suppressed ? null : r.composite?.score)},
        ...areaCols.map((c, i) => ({
            key: c.column_name, label: c.num ? `Context Area ${c.num}` : c.column_name, type: 'number',
            accessor: (r) => (r.areas[i]?.suppressed ? null : r.areas[i]?.score),
        })),
    ];
    const t = useTableControls(rows, columns);
    return (
        <>
            {(t.chips.length > 0 || rows.length >= 5) && <TableToolbar {...t}/>}
            <TableContainer sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflow: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', mb: 4}}>
                <Table sx={{minWidth: 700}}>
                    <TableHead>
                        <TableRow>
                            <SortFilterHeaderCell {...t.headerProps('school')} sx={{...headCell, textAlign: 'left', pl: 2.5}}/>
                            <SortFilterHeaderCell {...t.headerProps('composite')} sx={{...headCell, textAlign: 'center'}}/>
                            {areaCols.map((c) => (
                                <SortFilterHeaderCell key={c.column_name} {...t.headerProps(c.column_name)} sx={{...headCell, textAlign: 'center', backgroundColor: '#1A445C'}}/>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={2 + areaCols.length} sx={{...bodyCell, textAlign: 'center', color: '#6A7282', py: 3}}>
                                    No school results yet — shown once students complete the COS Children Assessment.
                                </TableCell>
                            </TableRow>
                        )}
                        {rows.length > 0 && t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={2 + areaCols.length} sx={{...bodyCell, textAlign: 'center', color: '#6A7282', py: 3}}>No school matches the filters.</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map((row) => (
                            <TableRow key={row.school}>
                                <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY, pl: 2.5}}>{row.school}</TableCell>
                                {row.composite && <TableCell sx={heat(row.composite)}>{show(row.composite)}</TableCell>}
                                {row.areas.map((c, i) => (
                                    <TableCell key={areaCols[i].column_name} sx={heat(c)}>{show(c)}</TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const CONTEXT_BY_SCHOOL_SECTION_CODE = 'S_8c';
const CARD_SECTIONS = [CONTEXT_BY_SCHOOL_SECTION_CODE];

const ContextBySchoolTab = () => {
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });

    if (loading) {
        return <CircularLoader />;
    }

    if (!dashboardData) {
        return <NoDataAvailable />;
    }
    const cols = findCard(dashboardData, CONTEXT_BY_SCHOOL_SECTION_CODE, 'C_2')?.table_objects || [];
    const schoolCol = cols.find((c) => c.column_type === 'text') || cols[0];
    const scoreCols = cols.filter((c) => c !== schoolCol);
    const compositeCol = scoreCols.find((c) => /composite/i.test(c.column_name));
    const AREA_COLS = scoreCols
        .filter((c) => c !== compositeCol)
        .map((c) => {
            const n = String(c.column_subname || '').match(/\d+/)?.[0];
            return {...c, num: n ? Number(n) : null};
        })
        .sort((a, b) => (a.num || 99) - (b.num || 99));

    // A school under the n floor (cell_data.suppressed) keeps its row but its cells show the placeholder, uncoloured.
    const cell = (col, i) => {
        const cd = col?.cell_data?.[i];
        const suppressed = isSuppressedCell(cd);
        return {score: suppressed ? null : whole(col?.values?.[i]), band: suppressed ? null : cd?.band, suppressed};
    };
    const SCHOOL_ROWS = (schoolCol?.values || []).map((school, i) => ({
        school,
        composite: compositeCol ? cell(compositeCol, i) : {score: null, band: undefined},
        areas: AREA_COLS.map((c) => cell(c, i)),
    }));

    const scored = SCHOOL_ROWS.filter((r) => r.composite && r.composite.score !== null);
    const lowest = scored.length > 1
        ? scored.reduce((min, r) => (r.composite.score < min.composite.score ? r : min), scored[0])
        : null;

    return (
    <Box>
        <PageHeader
            title="Context & Support by School"
            subtitle="Which schools need the most support, and in what area?"
        />

        <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY, mb: 2}}>
            Context & Support Area Scores by School
        </Typography>
        <Legend/>

        <SchoolHeatmapTable rows={SCHOOL_ROWS} areaCols={AREA_COLS}/>

        <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY, mb: 1.5}}>
            List of the Context & Support Areas
        </Typography>
        {AREA_COLS.filter((c) => c.num).map((c) => (
            <Typography key={c.column_name} sx={{fontSize: '13.5px', color: '#374151', mb: 0.5}}>
                <strong>Context Area {c.num}:</strong> {c.column_name}
            </Typography>
        ))}

        <GuidanceBanner
            label="Recommended action:"
            text={lowest
                ? `Patterns across schools guide where to focus. ${lowest.school} is the lowest school average; check its coverage before acting.`
                : 'Patterns across schools guide where to focus; check a school\'s coverage before acting.'}
        />
    </Box>
    );
};

export default ContextBySchoolTab;
